import React, { useCallback, useEffect, useState } from 'react';
import styles from './CustomThumbnailModalCustom.module.scss';
import { CustomButton } from '../Button/Button';
import ThumbnailPlayer from './ThumbnailPlayer';
import ThumbnailSlider from './ThumbnailSlider';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { THUMBNAIL_EDITING_MODE } from '../../hooks/useThumbnailChooserHook';

function CustomThumbnailModalContent({
  videoDuration,
  url,
  video,
  waveform,
  staticThumbnail,
  animatedThumbnail,
  processing,
  onSelect,
  onCancel,
}) {
  const [currentValue, setCurrentValue] = useState(null);
  const { t } = useTranslation();

  const [mode, setMode] = useState(THUMBNAIL_EDITING_MODE.STATIC);

  const [staticThumbnailTimestamp, setStaticThumbnailTimestamp] = useState(staticThumbnail?.properties?.timestamp ?? 0);
  const [animatedThumbnailTimestamp, setAnimatedThumbnailTimestamp] = useState(animatedThumbnail?.properties?.timestamp ?? 0);

  const [timestampChanged, setTimestampChanged] = useState({});

  const updateThumbnailTimestamp = useCallback(
    (mode, value) => {
      switch (mode) {
        case THUMBNAIL_EDITING_MODE.STATIC:
          setStaticThumbnailTimestamp(value);
          break;
        case THUMBNAIL_EDITING_MODE.ANIMATED:
          setAnimatedThumbnailTimestamp(value);
          break;
      }
      setTimestampChanged(prevState => ({ ...prevState, [mode]: true }));
    },
    [setStaticThumbnailTimestamp, setAnimatedThumbnailTimestamp, setTimestampChanged]
  );

  const [seekToValue, setSeekToValue] = useState(0.0);
  const [emptyThumbnail, setEmptyThumbnail] = useState(false);

  const onSeekChange = useCallback(
    value => {
      setCurrentValue(value);
      setEmptyThumbnail(false);
      updateThumbnailTimestamp(mode, value * (videoDuration / 1000));
      setSeekToValue(parseFloat(value * (videoDuration / 1000)));
    },
    [mode, video]
  );

  const handleStaticMode = () => {
    setMode(THUMBNAIL_EDITING_MODE.STATIC);
    if (staticThumbnailTimestamp * 1000 > videoDuration) {
      setEmptyThumbnail(true);
    } else {
      setEmptyThumbnail(false);
      setCurrentValue(staticThumbnailTimestamp / (videoDuration / 1000));
    }
  };

  const handleAnimatedMode = () => {
    setMode(THUMBNAIL_EDITING_MODE.ANIMATED);
    if (animatedThumbnailTimestamp * 1000 > videoDuration) {
      setEmptyThumbnail(true);
    } else {
      setEmptyThumbnail(false);
      setCurrentValue(animatedThumbnailTimestamp / (videoDuration / 1000));
    }
  };

  useEffect(() => {
    if (mode === THUMBNAIL_EDITING_MODE.STATIC) {
      if (staticThumbnailTimestamp * 1000 > videoDuration) {
        setEmptyThumbnail(true);
      }
      setCurrentValue(staticThumbnailTimestamp / (videoDuration / 1000));
    } else {
      if (animatedThumbnailTimestamp * 1000 > videoDuration) {
        setEmptyThumbnail(true);
      }
      setCurrentValue(animatedThumbnailTimestamp / (videoDuration / 1000));
    }
    return () => {
      setTimestampChanged({});
    };
  }, []);

  const resetThumbnailPosition = () => {
    setEmptyThumbnail(false);
    updateThumbnailTimestamp(mode, 0);
    onSeekChange(0);
  };

  const handleSelect = useCallback(() => {
    const payload = {};
    for (const key in timestampChanged) {
      if (key === THUMBNAIL_EDITING_MODE.ANIMATED) {
        payload.animated = animatedThumbnailTimestamp;
      }
      if (key === THUMBNAIL_EDITING_MODE.STATIC) {
        payload.static = staticThumbnailTimestamp;
      }
    }
    onSelect(payload);
  }, [timestampChanged, onSelect]);

  useEffect(() => {
    currentValue !== null && setSeekToValue(parseFloat(currentValue * (videoDuration / 1000)));
  }, [currentValue]);

  return (
    <>
      <div className={styles.modalContainer}>
        <ThumbnailPlayer
          url={url}
          seekToValue={seekToValue}
          animated={mode === THUMBNAIL_EDITING_MODE.ANIMATED}
          resetThumbnailPosition={resetThumbnailPosition}
          emptyThumbnail={emptyThumbnail}
        />
        <div className={styles.sliderContainer}>
          <ThumbnailSlider
            onSeekChange={onSeekChange}
            waveform={waveform}
            videoDuration={videoDuration}
            currentValue={currentValue}
            video={video}
            emptyThumbnail={emptyThumbnail}
          />
          <div className={styles.modalFooter}>
            <div className={styles.modeContainer}>
              <CustomButton
                variant="link"
                onClick={handleStaticMode}
                className={cx({ [styles.active]: mode === THUMBNAIL_EDITING_MODE.STATIC })}
              >
                <span>{t('my_video.tools.thumbnail.modal.static')}</span>
              </CustomButton>
              <CustomButton
                variant="link"
                onClick={handleAnimatedMode}
                className={cx({ [styles.active]: mode === THUMBNAIL_EDITING_MODE.ANIMATED })}
              >
                <span>{t('my_video.tools.thumbnail.modal.animated')}</span>
              </CustomButton>
            </div>
            <div className={styles.modalActionsContainer}>
              <CustomButton
                variant="secondary"
                label={t('general.cancel')}
                className={styles.cancelButton}
                onClick={onCancel}
                loadingText={t('general.cancel')}
              />
              <CustomButton
                disabled={emptyThumbnail}
                variant="primary"
                label={t('general.select')}
                onClick={handleSelect}
                loading={processing}
                loadingSpinner={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CustomThumbnailModalContent;
